import { z } from "zod";
import {
  MTC_MEN2_KEY,
  MTC_MEN2_QUESTION,
  readF01SafetyAnswer,
  TYPE_1_DIABETES_KEY,
  TYPE_1_DIABETES_QUESTION,
  type BmiQuizField,
  type F01SafetyAnswer,
} from "./bmi-quiz";

// TES-1164 (Ö-383) — type 1 diabetes and MTC/MEN 2 as the doctor reads them,
// whichever form asked.
//
// F-01 owns both questions from v10: "Svaren sparas på patientärendet och
// återanvänds i F-02, admin och läkarens beslutsunderlag." Every patient who
// reached F-02 before that answered MTC/MEN 2 there instead
// (`med_cancer_endocrine`), and intake v17 still asks both questions of a
// patient whose F-01 did not. So one answer per question is resolved here:
//   1. her latest F-01, when it asked the question;
//   2. otherwise her latest F-02 answer to it;
//   3. otherwise nothing: never asked, which is not "Nej".
// An older F-02 answer is therefore always shown when F-01 has none, and
// never replaced by a "not answered".
//
// The F-02 MTC question is wider than F-01's ("… eller annan relevant
// hormonproducerande tumör", "nära biologisk familj"), so each answer carries
// the question it was given to, verbatim.

export const F01_SAFETY_KEYS = [TYPE_1_DIABETES_KEY, MTC_MEN2_KEY] as const;
export type F01SafetyKey = (typeof F01_SAFETY_KEYS)[number];

/** The F-02 (intake) field that asked the same thing, per F-01 key. */
export const INTAKE_SAFETY_FIELD: Readonly<Record<F01SafetyKey, string>> = {
  [TYPE_1_DIABETES_KEY]: "med_type1_diabetes",
  [MTC_MEN2_KEY]: "med_cancer_endocrine",
};

// The F-02 question as it has stood since intake v3 (and the v17 fallback).
const INTAKE_QUESTION: Readonly<Record<F01SafetyKey, string>> = {
  [TYPE_1_DIABETES_KEY]: "Har du fått diagnosen diabetes typ 1?",
  [MTC_MEN2_KEY]:
    "Har du eller nära biologisk familj haft medullär sköldkörtelcancer, MEN2 eller annan relevant hormonproducerande tumör?",
};

// F-02's option values and labels (NJO, and v17's "Jag är osäker").
const INTAKE_ANSWER: Readonly<
  Record<F01SafetyKey, Record<string, [F01SafetyAnswer, string]>>
> = {
  [TYPE_1_DIABETES_KEY]: {
    ja: ["yes", "Ja"],
    nej: ["no", "Nej"],
    osaker: ["unsure", "Jag är osäker"],
  },
  [MTC_MEN2_KEY]: {
    ja: ["yes", "Ja"],
    nej: ["no", "Nej"],
    osaker: ["unsure", "Osäker"],
  },
};

const F01_FIELD: Readonly<Record<F01SafetyKey, BmiQuizField>> = {
  [TYPE_1_DIABETES_KEY]: TYPE_1_DIABETES_QUESTION,
  [MTC_MEN2_KEY]: MTC_MEN2_QUESTION,
};

export const f01SafetyAnswerSchema = z.object({
  key: z.enum(F01_SAFETY_KEYS),
  value: z.enum(["yes", "no", "unsure"]),
  // The question and her answer, verbatim (Swedish: the doctor reads it).
  question: z.string(),
  answer: z.string(),
  // F-01 (`f01`) or the health declaration (`intake`).
  source: z.enum(["f01", "intake"]),
  submittedAt: z.string(),
  templateVersion: z.number().int().nullable(),
});
export type F01SafetyAnswerRow = z.infer<typeof f01SafetyAnswerSchema>;

export interface SafetyAnswerSource {
  answers: Record<string, unknown>;
  submittedAt: Date;
  templateVersion: number | null;
}

/**
 * One row per question that was asked somewhere, in question order. A
 * question asked nowhere has no row.
 */
export function resolveF01SafetyAnswers(input: {
  f01: SafetyAnswerSource | null;
  intake: SafetyAnswerSource | null;
}): F01SafetyAnswerRow[] {
  const rows: F01SafetyAnswerRow[] = [];
  for (const key of F01_SAFETY_KEYS) {
    const f01Value = input.f01
      ? readF01SafetyAnswer(key, input.f01.answers[key])
      : null;
    if (input.f01 && f01Value !== null) {
      const field = F01_FIELD[key];
      const raw = input.f01.answers[key];
      rows.push({
        key,
        value: f01Value,
        question: field.label.sv,
        answer:
          field.options?.find((o) => o.value === raw)?.label.sv ?? String(raw),
        source: "f01",
        submittedAt: input.f01.submittedAt.toISOString(),
        templateVersion: input.f01.templateVersion,
      });
      continue;
    }
    const raw = input.intake?.answers[INTAKE_SAFETY_FIELD[key]];
    const intakeAnswer =
      typeof raw === "string" ? INTAKE_ANSWER[key][raw] : undefined;
    if (input.intake && intakeAnswer) {
      rows.push({
        key,
        value: intakeAnswer[0],
        question: INTAKE_QUESTION[key],
        answer: intakeAnswer[1],
        source: "intake",
        submittedAt: input.intake.submittedAt.toISOString(),
        templateVersion: input.intake.templateVersion,
      });
    }
  }
  return rows;
}

/**
 * Whether her latest F-01 left either question unasked (or there is no
 * F-01): F-02 then asks them (intake v17, context `f01_safety_unanswered`).
 */
export function f01SafetyUnanswered(
  answers: Record<string, unknown> | null,
): boolean {
  if (answers === null) return true;
  return F01_SAFETY_KEYS.some(
    (key) => readF01SafetyAnswer(key, answers[key]) === null,
  );
}
